import { useSearchParams, useNavigate } from "react-router";
import { useAccountLogout, useAccountState } from "@made-people/grease-account";
import { useHref, useLink } from "@made-people/grease-link";
import { useEffect, useRef } from "react";
import { MissMaryAccountOrders } from "./account-index/mm-account-orders/index";
import { useTranslate } from "@made-people/grease-translations";
import { MissMaryAccountOverview } from "./account-index/mm-account-overview";
import { MissMaryAccountDetails } from "./account-index/mm-account-details";

const HomeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 10.5L12 3l9 7.5V21a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 21V10.5z" />
    <path d="M9 22.5V12h6v10.5" />
  </svg>
);

const PackageIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    <path d="M3.27 6.96L12 12.01l8.73-5.05" />
    <path d="M12 22.08V12" />
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const SupportIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const LogOutIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const validRoutes = ["overview", "orders", "details"] as const;
type ValidRoute = (typeof validRoutes)[number];

const isValidRoute = (route: string | null): route is ValidRoute => {
  return validRoutes.includes(route as ValidRoute);
};

export const AccountIndexPageRoute = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const account = useAccountState();
  const logout = useAccountLogout();
  const href = useHref();
  const translate = useTranslate();
  const Link = useLink();
  const redirectAttempted = useRef(false);
  const currentRoute = searchParams.get("route") || "overview";
  const activeRoute = isValidRoute(currentRoute) ? currentRoute : "overview";

  useEffect(() => {
    if (account.customer === null && !redirectAttempted.current) {
      redirectAttempted.current = true;
      const loginUrl = href("comfort-club/login");
      navigate(loginUrl, { replace: true });
    }

    if (account.customer !== null && account.customer !== undefined) {
      redirectAttempted.current = false;
    }
  }, [account.customer, navigate, href]);

  if (!account?.customer) {
    return (
      <div className="mx-auto w-full max-w-screen-page-max-width md:px-component-spacing-lg xl:px-component-spacing-lg">
        <div className="w-full min-h-[80vh]" />
      </div>
    );
  }

  const menuItems = [
    {
      label: translate("Account_Menu_Overview"),
      href: "comfort-club",
      route: "overview" as ValidRoute,
      icon: HomeIcon,
    },
    {
      label: translate("Account_Menu_Orders"),
      href: "comfort-club?route=orders",
      route: "orders" as ValidRoute,
      icon: PackageIcon,
    },
    {
      label: translate("Account_Menu_Details"),
      href: "comfort-club?route=details",
      route: "details" as ValidRoute,
      icon: UserIcon,
    },
  ];

  const handleSignOut = () => {
    logout.execute().finally(() => {
      window.location.href = href("");
    });
  };

  return (
    <div className="mx-auto w-full max-w-screen-page-max-width md:px-component-spacing-lg xl:px-component-spacing-lg">
      <div className="w-full md:grid md:grid-cols-12 md:gap-x-1 md:items-start min-h-[80vh]">
        <div className="md:col-span-3 pt-4 lg:pt-14 md:!border-0 md:sticky md:top-0">
          <nav
            aria-label="Account navigation"
            className="flex flex-row gap-2 mb-8 px-4 overflow-x-auto md:hidden"
          >
            {menuItems.map((item) => {
              const isActive = activeRoute === item.route;
              const Icon = item.icon;
              return (
                <Link
                  key={item.route}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-body-sm shrink-0 whitespace-nowrap transition-colors ${
                    isActive ? "bg-primary text-white" : "bg-white text-primary"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <a
              href="https://support.missmary.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-body-sm shrink-0 whitespace-nowrap transition-colors"
            >
              <SupportIcon className="w-4 h-4 shrink-0" />
              <span>{translate("Account_Menu_Support")}</span>
            </a>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-body-sm shrink-0 whitespace-nowrap text-left transition-colors"
              onClick={handleSignOut}
            >
              <LogOutIcon className="w-4 h-4 shrink-0" />
              {translate("Account_Menu_SignOutButton")}
            </button>
          </nav>

          <nav
            aria-label="Account navigation"
            className="hidden md:flex lg:pr-16 md:flex-col md:gap-2 px-4 lg:px-0"
          >
            {menuItems.map((item) => {
              const isActive = activeRoute === item.route;
              const Icon = item.icon;
              return (
                <Link
                  key={item.route}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl text-body-sm lg:text-label-md transition-colors ${
                    isActive ? "bg-primary text-white" : "bg-white text-primary"
                  }`}
                >
                  <Icon className="w-6 h-6 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <a
              href="https://support.missmary.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white text-primary text-body-sm lg:text-label-md transition-colors"
            >
              <SupportIcon className="w-6 h-6 shrink-0" />
              <span>{translate("Account_Menu_Support")}</span>
            </a>
            <button
              type="button"
              className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white text-primary text-body-sm lg:text-label-md text-left transition-colors"
              onClick={handleSignOut}
            >
              <LogOutIcon className="w-6 h-6 shrink-0" />
              {translate("Account_Menu_SignOutButton")}
            </button>
          </nav>
        </div>
        <div className="w-full md:col-start-4 md:col-span-9 lg:pt-14  px-4 lg:px-0">
          {activeRoute === "overview" && <MissMaryAccountOverview />}
          {activeRoute === "orders" && <MissMaryAccountOrders />}
          {activeRoute === "details" && <MissMaryAccountDetails />}
        </div>
      </div>
    </div>
  );
};
